import { Search } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link } from 'react-router'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogClose, DialogContent } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { EmptyState, ErrorState } from '@/components/ui/states'
import type { ReplyTemplate } from '@/lib/api/types'
import { cn } from '@/lib/cn'
import { fillTemplate, rankTemplates, useReplyTemplates } from './reply-templates'

/**
 * Choose a reply template for this ticket: the ones for its category first, then those for any
 * ticket, each previewed with this ticket's details filled in.
 */
export function ReplyTemplatePicker({
  open,
  onOpenChange,
  category,
  values,
  onInsert,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  category: string | null
  values: Record<string, string>
  onInsert: (template: ReplyTemplate, text: string) => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title="Use a template"
        description="It goes into your reply with this ticket’s details filled in. Edit it before sending."
        className="max-w-4xl"
      >
        {open && <PickerBody category={category} values={values} onInsert={onInsert} />}
      </DialogContent>
    </Dialog>
  )
}

function PickerBody({
  category,
  values,
  onInsert,
}: {
  category: string | null
  values: Record<string, string>
  onInsert: (template: ReplyTemplate, text: string) => void
}) {
  const templates = useReplyTemplates()
  const [search, setSearch] = useState('')
  const [chosenId, setChosenId] = useState<string | null>(null)

  const list = useMemo(() => {
    const all = rankTemplates(templates.data?.data ?? [], category)
    const q = search.trim().toLowerCase()
    return q === '' ? all : all.filter((t) => `${t.title}\n${t.body}`.toLowerCase().includes(q))
  }, [templates.data, category, search])

  if (templates.isPending) return <Skeleton className="h-72" />
  if (templates.isError)
    return <ErrorState error={templates.error} onRetry={() => void templates.refetch()} />

  const labels = Object.fromEntries(templates.data.meta.categories.map((c) => [c.key, c.label]))
  const chosen = list.find((t) => t.id === chosenId) ?? list[0]
  const preview = chosen ? fillTemplate(chosen.body, values) : ''

  return (
    <div className="grid gap-4">
      <label className="relative">
        <span className="sr-only">Search templates</span>
        <Search
          className="pointer-events-none absolute top-1/2 left-3 size-3.5 -translate-y-1/2 text-ink-subtle"
          aria-hidden="true"
        />
        <Input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search titles and text"
          className="h-9 pl-8 text-[13px]"
          autoFocus
        />
      </label>
      {list.length === 0 ? (
        <EmptyState
          title="No template matches"
          description="Try other words, or write this reply yourself and save it as a template."
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-[minmax(0,16rem)_minmax(0,1fr)]">
          <ul className="grid max-h-[26rem] content-start gap-1 overflow-y-auto pr-1" aria-label="Templates">
            {list.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  onClick={() => setChosenId(t.id)}
                  aria-pressed={t.id === chosen?.id}
                  className={cn(
                    'grid w-full gap-0.5 rounded-md px-3 py-2 text-left text-[13px]',
                    t.id === chosen?.id
                      ? 'bg-accent-soft text-ink'
                      : 'text-ink-muted hover:bg-surface-sunken',
                  )}
                >
                  <span className="font-medium text-ink">{t.title}</span>
                  <span className="text-xs text-ink-subtle">
                    {t.category === null ? 'Any ticket' : (labels[t.category] ?? t.category)}
                    {category !== null && t.category === category ? ' · suggested' : ''}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          {chosen && (
            <section aria-label="Preview" className="grid content-start gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-semibold text-ink">{chosen.title}</h3>
                {chosen.ready_made ? (
                  <Badge tone="outline">Ready-made</Badge>
                ) : (
                  chosen.author && <Badge tone="neutral">By {chosen.author}</Badge>
                )}
              </div>
              <p className="max-h-[22rem] overflow-y-auto rounded-md border border-border bg-surface-sunken/50 p-3 text-[13px] leading-relaxed whitespace-pre-line text-ink">
                {preview}
              </p>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Link
                  to="/tickets/templates"
                  className="text-xs text-ink-muted underline-offset-2 hover:text-ink hover:underline"
                >
                  Manage templates
                </Link>
                <div className="flex gap-2">
                  <DialogClose asChild>
                    <Button variant="ghost">Cancel</Button>
                  </DialogClose>
                  <Button onClick={() => onInsert(chosen, preview)}>Insert into reply</Button>
                </div>
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  )
}
